// The stream-health badge (Brief 5 / pt10 + pt20) — the SHARED honesty cue both
// the Dashboard session header and the desktop channel header render, so they
// speak with one voice. Honest by construction: MOTION MEANS ALIVE.
// `live` shows a genuine ~1.5s heartbeat (visibly alive, not a faint breath a
// dead stream could fake); `connecting` / `reconnecting` show a quiet neutral
// pill that breathes an honest "we're trying"; `dead` STOPS moving and gets
// LOUDER — "stream lost — last update Xm ago" on a higher-emphasis pill, so the
// stillness reads as alarm and a dead feed is never the calmest thing on screen.
//
// Token-pure (var(--waytide-sys-*)); the live heartbeat and the reconnecting
// breath are both stilled under prefers-reduced-motion (where the dead state's
// emphasis still carries it — see streamHealth.css). NEVER the reserved
// signal-attention amber — stream-health is not a "needs you" call.

import type { StreamHealth } from "@waytide/core";
import { streamHealthCue } from "../model/streamHealth";
import "../styles/streamHealth.css";

export function StreamHealthBadge({
  health,
  lastEventAt,
  now,
}: {
  health: StreamHealth;
  lastEventAt: number | null;
  /** Injectable clock for deterministic tests; defaults to wall-clock. */
  now?: number;
}) {
  const cue = streamHealthCue(health, lastEventAt, now);

  // Live: a single faint ambient heartbeat dot, no words — calm-competence.
  if (cue.live) {
    return (
      <span
        className="wt-stream-health wt-stream-health--live"
        data-stream-health="live"
        role="status"
        aria-label="Stream live"
      >
        <span className="wt-stream-health__beat" aria-hidden="true" />
      </span>
    );
  }

  // Not live: a quiet neutral pill that says, honestly, what we don't know.
  return (
    <span
      className="wt-stream-health wt-stream-health--off"
      data-stream-health={health}
      data-tone={cue.tone}
      role="status"
    >
      <span className="wt-stream-health__dot" aria-hidden="true" />
      <span className="wt-stream-health__label">{cue.label}</span>
    </span>
  );
}
